import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Icon, DateTimePicker, Counter, GroupedInputBox, LabeledField, SEO } from '../../../components/common';
import TransferLanding from '../../../components/service/TransferLanding';
import { useSearchFlow, SearchFlowModals } from '../../../components/service/SearchFlow';
import SearchButton from '../../../components/service/SearchButton';
import AddressSearch from '../../../components/service/AddressSearch';
import { useFieldGuide } from '../../../hooks/useFieldGuide';
import { useSameDepartureArrival } from '../../../hooks/useSameDepartureArrival';
import useHolidayWarning from '../../../hooks/useHolidayWarning';
import HolidayWarningModal from '../../../components/service/HolidayWarningModal';
import { pricingService } from '../../../api/services';
import { loadRecentSearch, saveRecentSearch } from '../../../hooks/useRecentSearch';
import { usePendingSearch } from '../../../hooks/usePendingSearch';
import { getDefaultBookingDateTime } from '../../../utils/dateUtils';

const FIELDS = ['departure', 'destination', 'dateTime', 'people'];

export default function TransferPet() {
  const { t } = useTranslation();
  const flow = useSearchFlow('pet');
  const { isHighlighted, onFocusField, wrapChange } = useFieldGuide(FIELDS);
  const recent = loadRecentSearch('pet', 1);
  const [departure, setDeparture] = useState('');
  const [departureName, setDepartureName] = useState('');
  const [departureNameI18n, setDepartureNameI18n] = useState(''); // #216: 표시 전용 현지화 명칭
  const [destination, setDestination] = useState('');
  const [destinationName, setDestinationName] = useState('');
  const [destinationNameI18n, setDestinationNameI18n] = useState(''); // #216: 표시 전용 현지화 명칭 (mobile/desktop 공유)
  const { sameAsDeparture, setSameAsDeparture, handleSameAsDeparture } = useSameDepartureArrival(departure, setDestination, { departureName, departureNameI18n, setDestinationName, setDestinationNameI18n });
  const [dateTime, setDateTime] = useState(() => getDefaultBookingDateTime());
  const [people, setPeople] = useState(1);
  const [waitText, setWaitText] = useState('');

  const { holidayInfo } = useHolidayWarning(dateTime);
  const [holidayModal, setHolidayModal] = useState(null);
  const pendingSearchRef = useRef(null);

  // Restore pending search data after login redirect
  usePendingSearch('pet', useCallback((data) => {
    if (data.departure) setDeparture(data.departure);
    if (data.destination) setDestination(data.destination);
    if (data.dateTime) setDateTime(data.dateTime);
    if (data.people) setPeople(data.people);
  }, []));

  useEffect(() => {
    // Row 45: usageTimeBadge는 BE에서 한글로 내려오므로 다국어 i18n 키를 직접 사용
    setWaitText(t('form.base_time_2h'));
  }, [t]);

  return (
    <>
    <SEO serviceType="Pet-Friendly Transport" renderH1 />
    <TransferLanding
      heroImg="pet"

    >
      {waitText && <Badge variant="time">{waitText}</Badge>}

      {/* Row 1: 출발지 → 도착지 */}
      <GroupedInputBox className="lg:hidden">
        <AddressSearch value={departure} displayName={departureNameI18n || departureName} onChange={(address, name, nameI18n) => { setDeparture(address); setDepartureName(name || ''); setDepartureNameI18n(nameI18n || name || ''); }} placeholder={t('form.select_departure')} variant="borderless" restriction={{ message: t('form.region_restriction_seoul_only') }} />
        <AddressSearch value={sameAsDeparture ? departure : destination} displayName={sameAsDeparture ? (departureNameI18n || departureName) : destinationNameI18n} onChange={(address, name, nameI18n) => { setSameAsDeparture(false); setDestination(address); setDestinationName(name || ''); setDestinationNameI18n(nameI18n || name || ''); }} placeholder={t('form.select_destination')} variant="borderless" restriction={{ message: t('form.region_restriction_seoul_only') }} />
      </GroupedInputBox>
      <div className="hidden lg:flex lg:items-start gap-2.5">
        <LabeledField label={t('form.departure')} highlighted={isHighlighted('departure')} onFocusCapture={() => onFocusField('departure')} hint={t('form.region_restriction_seoul_only')}>
          <AddressSearch
            value={departure}
            displayName={departureNameI18n || departureName}
            onChange={(address, name, nameI18n) => { wrapChange('departure', setDeparture)(address); setDepartureName(name || ''); setDepartureNameI18n(nameI18n || name || ''); }}
            placeholder={t('form.select_departure')}
            restriction={{ message: t('form.region_restriction_seoul_only') }}
          />
        </LabeledField>
        <div className="flex items-center justify-center w-6 h-12 mt-6">
          <Icon name="arrow-oneway" size={24} className="text-mono-300" />
        </div>
        <div className="flex-1 flex flex-col gap-1">
          <LabeledField label={t('form.destination')} highlighted={isHighlighted('destination')} onFocusCapture={() => onFocusField('destination')} hint={t('form.region_restriction_seoul_only')}>
            <AddressSearch
              value={sameAsDeparture ? departure : destination}
              displayName={sameAsDeparture ? (departureNameI18n || departureName) : destinationNameI18n}
              onChange={(address, name, nameI18n) => { wrapChange('destination', (a) => { setSameAsDeparture(false); setDestination(a); })(address); setDestinationName(name || ''); setDestinationNameI18n(nameI18n || name || ''); }}
              placeholder={t('form.select_destination')}
              restriction={{ message: t('form.region_restriction_seoul_only') }}
            />
          </LabeledField>
          {departure && (
            <button
              type="button"
              onClick={handleSameAsDeparture}
              className={`inline-flex items-center gap-1.5 text-2sm font-medium transition ${
                sameAsDeparture ? 'text-orange-500' : 'text-mono-500 hover:text-mono-700'
              }`}
            >
              <Icon name={sameAsDeparture ? 'check-filled' : 'check-circle'} size={16} />
              {t('form.same_as_departure')}
            </button>
          )}
        </div>
      </div>

      {/* Row 2: 차량 이용 일시 + 이용 인원 */}
      <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-11">
        <LabeledField label={t('form.vehicle_use_datetime')} highlighted={isHighlighted('dateTime')} onFocusCapture={() => onFocusField('dateTime')}>
          <DateTimePicker value={dateTime} onChange={wrapChange('dateTime', setDateTime)} maxMonths={2} blockHolidays />
        </LabeledField>
        <LabeledField label={t('form.use_passengers')} highlighted={isHighlighted('people')} onFocusCapture={() => onFocusField('people')}>
          <Counter value={people} onChange={wrapChange('people', setPeople)} />
        </LabeledField>
      </div>

      <SearchButton onClick={() => {
        if (!departure) return alert(t('form.alert_input_departure'));
        if (!destination && !sameAsDeparture) return alert(t('form.alert_input_destination'));
        if (!dateTime) return alert(t('form.alert_select_datetime'));
        // BugHerd #87 회귀 fix: blocked + surcharge 두 케이스 모두 모달 노출.
        const proceed = () => {
          saveRecentSearch('pet', { departure, dateTime, people, destinationAddress: sameAsDeparture ? departure : destination });
          flow.startSearch(dateTime, {}, {
            departure, departureName,
            destination: sameAsDeparture ? departure : destination,
            destinationName: sameAsDeparture ? departureName : destinationName,
            people,
          });
        };
        if (holidayInfo?.blocked) { setHolidayModal(holidayInfo); return; }
        if (holidayInfo?.amount > 0) { pendingSearchRef.current = proceed; setHolidayModal(holidayInfo); return; }
        proceed();
      }} />
    </TransferLanding>
    <SearchFlowModals flow={flow} dateTime={dateTime} />
    {holidayModal && <HolidayWarningModal
      holiday={holidayModal}
      onClose={() => setHolidayModal(null)}
      onConfirm={() => { const fn = pendingSearchRef.current; pendingSearchRef.current = null; fn?.(); }}
    />}
    </>
  );
}
